import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import * as React from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { LightTooltip } from "../tooltip/ToolTip";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

const AddLocation = (props) => {
  const { closeDialog } = props;
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const resetForm = (props) => {
    // clear input fields
    var now = new Date();
    props.resetForm();
    setSelectedLocation("");
    setSelectedLocationKey(now);
  };

  const closeForm = (message) => {
    closeDialog(message);
  };

  // Locations
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(
    locationsOpen && locations.length === 0
  );
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationKey, setSelectedLocationKey] = useState("locationKey");
  const [locationEmpty, setLocationEmpty] = useState(false);
  const LOCATIONS_URL = "api/v1/locations";
  const handleLocationChange = (event, value) => {
    setSelectedLocation(value.id);
    setLocationEmpty(false);
  };

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!locationLoading) {
      return undefined;
    }

    const getLocations = async () => {
      try {
        const response = await axiosPrivate.get(LOCATIONS_URL, {
          signal: controller.signal,
        });
        isMounted && setLocations(response.data.data);
        setLocationLoading(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setLocationLoading(false);
      }
    };

    getLocations();

    return () => {
      isMounted = false;
      controller.abort();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [locationLoading]);

  React.useEffect(() => {
    if (!locationsOpen) {
      setLocations([]);
    }
  }, [locationsOpen]);

  // Validate form
  const validationSchema = Yup.object().shape({});

  // Common
  const initialValues = {
    locationId: selectedLocation,
    tenantId: auth.tenantId,
  };

  function onSubmit(values, props) {
    // No location picked
    if (selectedLocation === "") {
      setLocationEmpty(true);
      props.setSubmitting(false);
      return;
    }

    props.setSubmitting(true);
    addLocation(values, props);
  }

  const TENANT_URL = "/api/v1/tenants/add-branch";
  const addLocation = async (values, props) => {
    // Request payload
    const data = {
      locationId: selectedLocation,
      tenantId: auth.tenantId,
    };

    try {
      const response = await axiosPrivate.post(TENANT_URL, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      //console.log(response.data.message);
      props.setSubmitting(false);
      resetForm(props);
      closeForm(response.data.message);
    } catch (error) {
      props.setSubmitting(false);
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: `Failed To Add Location`,
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: { onSubmit },
      });
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  return (
    <Grid>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Stack spacing={2} mt={3} direction="column">
              <Autocomplete
                id="location"
                fullWidth
                onChange={handleLocationChange}
                key={selectedLocationKey}
                open={locationsOpen}
                onOpen={() => {
                  setLocationLoading(true);
                  setLocationsOpen(true);
                }}
                onClose={() => {
                  setLocationLoading(false);
                  setLocationsOpen(false);
                }}
                isOptionEqualToValue={(location, value) =>
                  location.name === value.name
                }
                getOptionLabel={(location) =>
                  location.name ? location.name : ""
                }
                options={locations}
                loading={locationLoading}
                loadingText="Loading locations..."
                noOptionsText="No locations"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    name="location"
                    error={locationEmpty}
                    helperText={locationEmpty ? "Location required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {locationLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>

            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
              <LightTooltip title="Clear form">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 8,
                    width: 120,
                    backgroundColor: "#808080",
                    padding: "7px 14px",
                    fontSize: "14px",
                    color: "white",
                    ":hover": {
                      bgcolor: "#696969",
                      color: "white",
                    },
                  }}
                  onClick={() => {
                    resetForm(props);
                  }}
                >
                  Reset
                </Button>
              </LightTooltip>

              <LightTooltip title="Submit details">
                <Button
                  loading={props.isSubmitting}
                  loadingPosition="start"
                  disabled={props.isSubmitting}
                  type="submit"
                  sx={{
                    width: 120,
                  }}
                  variant="solid"
                >
                  {props.isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </LightTooltip>
            </Stack>
          </Form>
        )}
      </Formik>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Grid>
  );
};

export default AddLocation;
